import { useMemo } from "react";

import {
  QuestionPreviewData,
  QuestionPreviewNext,
} from "@vericus/cadmus-common";
import { Alert } from "@vericus/cadmus-ui";

import { AnswerBlockFragment } from "@/generated/graphql";
import { isSnapshotEmpty, parseBlockSnapshot } from "@/stores/snapshot";

import {
  mapFieldInteraction,
  mapQuestionType,
  mapSnapshotToAnswer,
} from "../../utils/map-graphql-to-json-schema-types";
import * as styles from "./answer-preview.css";

interface Props {
  answerBlock: AnswerBlockFragment;
  referencesAnswerBlock: AnswerBlockFragment | null;
  /** Whether showing alert for unanswered question */
  showAlertOnUnAnswered?: boolean;
  /** Whether answer should be shuffled */
  shuffle?: boolean;
}

/**
 * Single row in the Answer Preview page.
 */
export function AnswerPreviewItem(props: Props) {
  const {
    answerBlock,
    referencesAnswerBlock,
    showAlertOnUnAnswered = false,
    shuffle,
  } = props;

  const question = answerBlock.question;

  const [snapshot, isEmpty] = useMemo(() => {
    const blockSnapshot = answerBlock.latestBlockSnapshot;
    const snapshot = blockSnapshot && parseBlockSnapshot(blockSnapshot);
    const questionType = question?.questionType;
    const isEmpty = questionType
      ? isSnapshotEmpty(questionType, snapshot ?? undefined)
      : null;
    return [snapshot, isEmpty];
  }, [answerBlock, question?.questionType]);

  const [referencesSnapshot, referencesIsEmpty] = useMemo(() => {
    const blockSnapshot = referencesAnswerBlock?.latestBlockSnapshot ?? null;
    const snapshot = blockSnapshot && parseBlockSnapshot(blockSnapshot);
    const questionType = question?.questionType;
    const isEmpty = questionType
      ? isSnapshotEmpty(questionType, snapshot ?? undefined)
      : null;
    return [snapshot, isEmpty];
  }, [referencesAnswerBlock, question?.questionType]);

  const questionData: QuestionPreviewData | null = useMemo(() => {
    if (question) {
      return {
        question_type: mapQuestionType(question.questionType),
        max_score: answerBlock.points ?? null,
        answer: mapSnapshotToAnswer(snapshot, question.questionType),
        references_answer: mapSnapshotToAnswer(
          referencesSnapshot,
          question.questionType
        ),
        fields: question.body?.fields
          ? question.body.fields.flatMap((field) => {
              return {
                identifier: field.identifier,
                response: defaultResponse(),
                interaction: mapFieldInteraction(field.interaction),
              };
            })
          : null,
        question_outcome: null,
        prompt_image: question.body?.promptImage ?? null,
        field_outcomes: [],
        shuffle_seed: shuffle === true ? answerBlock.id : null,
      };
    }
    return null;
  }, [answerBlock, snapshot, referencesSnapshot, question, shuffle]);

  const blockAttachments = answerBlock.blockAttachments.map((attachment) => ({
    name: attachment.name,
    media_id: attachment.id,
    media_url: attachment.mediaUrl,
    file_extension: attachment.fileExtension,
    file_size: attachment.fileSize,
  }));

  return (
    <div className={styles.answer}>
      <QuestionPreviewNext
        mode="answer"
        questionId={answerBlock.id}
        questionTitle={`Question ${answerBlock.orderLabel}`}
        promptDoc={answerBlock.question?.body?.promptDoc ?? null}
        bookmarked={answerBlock.bookmark ?? undefined}
        data={questionData}
        attachmentEnabled
        blockAttachments={blockAttachments}
      />
      {showAlertOnUnAnswered &&
        isEmpty === true &&
        referencesIsEmpty === true && (
          <div className={styles.section}>
            <Alert size="sm" title="You have not answered this question" />
          </div>
        )}
    </div>
  );
}

/**
 * Default field response since we hide response object from students
 */
function defaultResponse() {
  return {
    partial_scoring: null,
    match_similarity: null,
    error_margin: null,
    correct_values: [],
    correct_ranges: [],
    case_sensitive: null,
    cardinality: "single" as const,
    base_type: "string" as const,
  };
}
